import React, { useState } from 'react';
import { useCart, formatBookPrice, isHorsSerie } from '../context';
import { downloadBonDeCommande } from '../utils/bonDeCommande';

const formatEuro = (n: number): string =>
  Number.isInteger(n) ? String(n) : n.toFixed(2).replace('.', ',');

export const CartWidget: React.FC = () => {
  const { items, count, total, removeItem, clear } = useCart();
  const [open, setOpen] = useState(false);

  return (
    <div className='fixed bottom-4 right-4 z-40 flex flex-col items-end'>
      {/* Panneau du panier */}
      {open && (
        <div className='mb-3 w-[90vw] max-w-sm bg-white rounded-lg shadow-2xl border border-slate-200 flex flex-col max-h-[70vh]'>
          <div className='flex items-center justify-between px-4 py-3 border-b'>
            <h3 className='font-semibold text-gray-800'>
              Mon panier{count > 0 ? ` (${count})` : ''}
            </h3>
            <button
              onClick={() => setOpen(false)}
              aria-label='Fermer le panier'
              className='text-gray-400 hover:text-gray-600'
            >
              <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
              </svg>
            </button>
          </div>

          {count === 0 ? (
            <p className='px-4 py-8 text-center text-gray-500 text-sm'>
              Votre panier est vide.<br />
              Sélectionnez des ouvrages à commander.
            </p>
          ) : (
            <>
              <ul className='overflow-y-auto divide-y divide-slate-100 flex-1'>
                {items.map((book) => (
                  <li key={book.id} className='flex items-start gap-2 px-4 py-3'>
                    <div className='flex-1 min-w-0'>
                      {isHorsSerie(book) ? (
                        <span className='text-xs text-rose-600 font-semibold'>
                          Hors-série
                        </span>
                      ) : (
                        <span className='text-xs text-amber-700 font-semibold'>
                          N° {book.edition_num}
                        </span>
                      )}
                      <p className='text-sm text-gray-800 leading-snug'>
                        {book.title}
                      </p>
                    </div>
                    <span className='text-sm font-semibold text-gray-700 whitespace-nowrap'>
                      {formatBookPrice(book)}
                    </span>
                    <button
                      onClick={() => removeItem(book)}
                      aria-label='Retirer'
                      className='text-gray-300 hover:text-red-500 mt-0.5'
                    >
                      <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>

              <div className='px-4 py-3 border-t bg-slate-50'>
                <div className='flex items-center justify-between mb-3'>
                  <span className='text-sm text-gray-600'>Total</span>
                  <span className='text-lg font-bold text-gray-800'>
                    {formatEuro(total)} €
                  </span>
                </div>
                <button
                  onClick={() => downloadBonDeCommande(items)}
                  className='w-full bg-amber-600 hover:bg-amber-700 text-white font-medium py-2.5 rounded-lg transition mb-2'
                >
                  Télécharger le bon de commande
                </button>
                <button
                  onClick={clear}
                  className='w-full text-sm text-gray-500 hover:text-gray-700 py-1'
                >
                  Vider le panier
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Bouton flottant */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label='Ouvrir le panier'
        className='relative bg-amber-600 hover:bg-amber-700 text-white rounded-full w-14 h-14 shadow-lg flex items-center justify-center transition'
      >
        <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.8} d='M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z' />
        </svg>
        {count > 0 && (
          <span className='absolute -top-1 -right-1 bg-white text-amber-700 text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center border-2 border-amber-600'>
            {count}
          </span>
        )}
      </button>
    </div>
  );
};
